import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import classnames from "classnames";
import { useResizeDetector } from "react-resize-detector";
import { useLogger, usePdfWorkerSrc } from "../../services/hooks/index.js";
import { Icon } from "../../Icon/index.js";
import { useIsDesktop } from "../../hooks/index.js";
import { Box } from "../../Box/index.js";
import { CircularProgress } from "../../CircularProgress/index.js";
import { Button } from "../../Button/index.js";
import { ButtonGroup } from "../../ButtonGroup/index.js";
import { PreviewButtons } from "./Buttons/index.js";
import { useStyles } from "./Preview.styles.js";
const options = {
    isEvalSupported: false
};
const MIN_WIDTH_DISPLAY_BUTTONS = 174;
const PreviewPdf = ({ url, shape, onClickPrint, onOpen, onClickDownload, onLoadSuccess, withCollapsableFooter, onExpand })=>{
    const { classes } = useStyles({});
    const isDesktop = useIsDesktop();
    const pdfWorkerSrc = usePdfWorkerSrc();
    if (pdfjs.GlobalWorkerOptions.workerSrc !== pdfWorkerSrc) pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;
    const [pagesCount, setPagesCount] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [nextPageNumber, setNextPageNumber] = useState(1);
    const [isLoaded, setIsLoaded] = useState(false);
    const logger = useLogger();
    const { width, ref } = useResizeDetector({
        refreshMode: 'debounce',
        refreshRate: 100
    });
    useEffect(()=>{
        setPageNumber(1);
        setNextPageNumber(1);
    }, [
        url
    ]);
    const handleOpen = useCallback(()=>onOpen && onOpen(), [
        onOpen
    ]);
    const handleLoadSuccess = useCallback(({ numPages })=>{
        setPagesCount(numPages);
        setIsLoaded(true);
        null == onLoadSuccess || onLoadSuccess();
    }, [
        setPagesCount,
        setIsLoaded,
        onLoadSuccess
    ]);
    const handleSetPage = useCallback(()=>setPageNumber(nextPageNumber), [
        nextPageNumber,
        setPageNumber
    ]);
    const handlePrev = useCallback((event)=>{
        event.preventDefault();
        event.stopPropagation();
        setNextPageNumber((x)=>Math.max(1, x - 1));
    }, [
        setNextPageNumber
    ]);
    const handleNext = useCallback((event_0)=>{
        event_0.preventDefault();
        event_0.stopPropagation();
        setNextPageNumber((x_0)=>Math.min(null != pagesCount ? pagesCount : 0, x_0 + 1));
    }, [
        setNextPageNumber,
        pagesCount
    ]);
    const handleLoadError = (error)=>{
        logger.error(error, {
            tags: {
                vkdoc_error_type: 'pdf'
            }
        });
    };
    return /*#__PURE__*/ jsxs("div", {
        ref: ref,
        className: classnames(classes.root, classes.collapseWrapper, 'aqa_file_preview_pdf'),
        children: [
            'square' === shape && /*#__PURE__*/ jsx("div", {
                className: classes.square
            }),
            /*#__PURE__*/ jsx(Box, {
                onClick: handleOpen,
                children: /*#__PURE__*/ jsx(Document, {
                    options: options,
                    onLoadError: handleLoadError,
                    onSourceError: handleLoadError,
                    className: classnames(classes.document, 'square' === shape && classes.document_square),
                    file: url,
                    error: /*#__PURE__*/ jsx(Box, {
                        p: "32",
                        color: "text.light.secondary",
                        textAlign: "center",
                        children: "Ошибка при загрузке файла"
                    }),
                    loading: /*#__PURE__*/ jsx(Box, {
                        display: "flex",
                        justifyContent: "center",
                        p: "32",
                        children: /*#__PURE__*/ jsx(CircularProgress, {
                            size: 50
                        })
                    }),
                    onLoadSuccess: handleLoadSuccess,
                    children: [
                        /*#__PURE__*/ jsx(Page, {
                            width: null === width ? 1 : width,
                            pageNumber: pageNumber,
                            renderAnnotationLayer: true
                        }, pageNumber),
                        nextPageNumber !== pageNumber && /*#__PURE__*/ jsx(Page, {
                            className: classes.hiddenPage,
                            width: null === width ? 1 : width,
                            pageNumber: nextPageNumber,
                            onLoadSuccess: handleSetPage,
                            renderAnnotationLayer: true
                        }, nextPageNumber)
                    ]
                })
            }),
            /*#__PURE__*/ jsxs("div", {
                className: classes.pagination,
                children: [
                    null !== pagesCount && pagesCount > 1 && (null != width ? width : 0) > MIN_WIDTH_DISPLAY_BUTTONS && /*#__PURE__*/ jsxs(ButtonGroup, {
                        variant: "contained",
                        size: "small",
                        onClick: (event_1)=>event_1.stopPropagation(),
                        children: [
                            /*#__PURE__*/ jsx(Button, {
                                disabled: nextPageNumber <= 1,
                                onClick: handlePrev,
                                icon: /*#__PURE__*/ jsx(Icon, {
                                    name: "arrow_left_outline",
                                    size: "16"
                                })
                            }),
                            /*#__PURE__*/ jsx(Button, {
                                disabled: true,
                                children: /*#__PURE__*/ jsxs(Box, {
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    width: 38,
                                    children: [
                                        pageNumber,
                                        "/",
                                        pagesCount
                                    ]
                                })
                            }),
                            /*#__PURE__*/ jsx(Button, {
                                onClick: handleNext,
                                icon: /*#__PURE__*/ jsx(Icon, {
                                    name: "arrow_right_outline",
                                    size: "16"
                                })
                            })
                        ]
                    }),
                    onExpand && isDesktop && /*#__PURE__*/ jsx(Button, {
                        icon: /*#__PURE__*/ jsx(Icon, {
                            name: "fullscreen_outline",
                            size: "20"
                        }),
                        size: "small",
                        onClick: onExpand
                    })
                ]
            }),
            (onClickDownload || onClickPrint) && isLoaded && /*#__PURE__*/ jsx(PreviewButtons, {
                pdf: true,
                onClickDownload: onClickDownload,
                withCollapsableFooter: withCollapsableFooter,
                onClickPrint: onClickPrint
            })
        ]
    });
};
export { PreviewPdf };
